import { datadogRum } from '@datadog/browser-rum';

import { ddIgnorableErrors } from '~/constants/ddIgnorableErrors';

const datadogOptions = {
  applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID || '',
  clientToken: process.env.NEXT_PUBLIC_DATADOG_TOKEN || '',
  env: process.env.NEXT_PUBLIC_DATADOG_ENV,
  version: process.env.NEXT_PUBLIC_APP_RELEASE,
  site: 'datadoghq.eu',
  service: 'sso',
  sessionSampleRate:
    process.env.NEXT_PUBLIC_DATADOG_ENV === 'production' ? 100 : 50,
  sessionReplaySampleRate:
    process.env.NEXT_PUBLIC_DATADOG_ENV === 'production' ? 100 : 0,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  enableExperimentalFeatures: ['feature_flags'],
  actionNameAttribute: 'track-id',
  allowedTracingUrls: [
    process.env.NEXT_PUBLIC_ORCA_API_RESOURCE,
    process.env.NEXT_PUBLIC_AUTH_RESOURCE,
    process.env.NEXT_PUBLIC_USER_MANAGEMENT_URL,
  ],
  trackSessionAcrossSubdomains: true,
  beforeSend: event => {
    if (
      event.type === 'error' &&
      ddIgnorableErrors.some(error => event.error.message.includes(error))
    ) {
      return false;
    }
    return true;
  },
};

/**
 * Captures an exception by forwarding it to Datadog RUM with contextual data.
 * @param {Error} err - The thrown error.
 * @param {Object} [additionalData] - Extra context for richer error tracking.
 * @param {Object} [additionalData.req] - Next.js request value (SSR).
 * @param {Object} [additionalData.res] - Next.js response value (SSR).
 * @param {Object} [additionalData.errorInfo] - Info from a React component (`componentStack` is used for the wrapped stack).
 * @param {string} [additionalData.level] - Log level for the event.
 * @param {*} [additionalData.query] - Next.js query value (client).
 * @param {*} [additionalData.pathname] - Next.js pathname value (client).
 * @param {Object} [additionalData.reduxStore] - Redux store (its state will be attached as context).
 * @param {Object} [additionalData.tags] - Tags forwarded to Datadog.
 * @returns {void}
 */
const captureException = (err, additionalData) => {
  if (!(err instanceof Error)) {
    return;
  }

  const wrapped = new Error(err?.message || 'Unknown error');
  wrapped.stack =
    additionalData?.errorInfo?.componentStack ||
    err.stack ||
    'No stack trace available';
  wrapped.cause = err;

  const context = {};
  const statusCode = err.statusCode ?? additionalData?.res?.statusCode;
  if (statusCode != null) context.statusCode = statusCode;

  if (additionalData) {
    const {
      req = {},
      errorInfo,
      level,
      query,
      pathname,
      reduxStore,
      tags,
    } = additionalData;

    if (level) context.level = level;
    if (tags) context.tags = tags;

    if (typeof window !== 'undefined') {
      context.ssr = false;
      context.query = query;
      context.pathname = pathname;
    } else {
      context.ssr = true;
      context.url = req.url;
      context.method = req.method;
      context.headers = req.headers;
      context.params = req.params;
      context.requestQuery = req.query;
    }

    if (errorInfo) {
      Object.keys(errorInfo).forEach(key => {
        if (key !== 'componentStack') {
          context[key] = errorInfo[key];
        }
      });
    }

    if (reduxStore) {
      context.reduxState = reduxStore.getState();
    }
  }

  datadogRum.addError(wrapped, context);
};

datadogRum.init(datadogOptions);

export { captureException };
